import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Eye, EyeOff, Lock, User, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { validateCredentials, type ValidationResult } from '../services/authService';
import './ValidarCredenciais.css';
import './Login.css';
import logo from '../assets/UNIFAP-1.png';

const ValidarCredenciais: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [formData, setFormData] = useState({
    login: '',
    senha: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const validationResult = await validateCredentials(formData.login, formData.senha);
      setResult(validationResult);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTryAgain = () => {
    setResult(null);
    setFormData({ login: '', senha: '' });
  };

  const isValid = result?.valid ?? false;

  return (
    <div className="validar-page">
      <div className="validar-background" />

      <div className="validar-container">
        <motion.div
          className="validar-card glass"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="validar-header">
            <Link to="/" className="back-link">
              <ArrowLeft size={20} />
              <span>Voltar ao Portal</span>
            </Link>

            <div className="validar-logo">
              <div className="logo-icon">
                <img src={logo} alt="Logo UNIFAP" className="logo-icon-logo" />
              </div>
              <div>
                <h1>UNIFAP</h1>
                <p>Portal de Serviços</p>
              </div>
            </div>
          </div>

          {!result ? (
            <div className="validar-content">
              <h2>Validar Credenciais</h2>
              <p>
                Informe seu usuário e senha dos sistemas SIG para verificar se suas credenciais estão
                corretas. Nenhum acesso será realizado — apenas a validação dos dados.
              </p>

              <form onSubmit={handleSubmit} className="login-form validar-form">
                <div className="form-group">
                  <label htmlFor="login">Usuário</label>
                  <div className="input-wrapper">
                    <User size={20} />
                    <input
                      type="text"
                      id="login"
                      name="login"
                      value={formData.login}
                      onChange={handleInputChange}
                      placeholder="Digite seu usuário"
                      required
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="senha">Senha</label>
                  <div className="input-wrapper">
                    <Lock size={20} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="senha"
                      name="senha"
                      value={formData.senha}
                      onChange={handleInputChange}
                      placeholder="Digite sua senha"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  className="validar-submit-btn"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Verificando...' : 'Validar Credenciais'}
                </motion.button>
              </form>
            </div>
          ) : (
            <div className={`validar-content ${isValid ? 'success' : 'error'}`}>
              <div className="validar-icon">
                {isValid ? <CheckCircle2 size={64} /> : <XCircle size={64} />}
              </div>

              <h2>{isValid ? 'Credenciais confirmadas' : 'Credenciais não confirmadas'}</h2>

              <p>{result.message}</p>

              {!isValid && (
                <a
                  href="https://sig.unifap.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="validar-sig-link"
                >
                  Atualizar dados no sistema SIG
                </a>
              )}

              <div className="validar-actions">
                <Link to="/" className="validar-btn primary">
                  Voltar ao início
                </Link>
                {!isValid && (
                  <button type="button" className="validar-btn secondary" onClick={handleTryAgain}>
                    Tentar novamente
                  </button>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ValidarCredenciais;
